import React, { useEffect, useState } from 'react';
import FeeBoardView from './FeeBoard';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../i18n';
import '../styles/global.css';
import { useStore } from '../store';
import { api } from '../utils/api';
import { isValidModule } from '../modules';
import { isSandbox, setSandbox } from '../services/sandboxMode';
import Portal from './Portal';
import MailApp from './MailApp';
import ModuleStub from './ModuleStub';
import Admin from './Admin';
import PettyCash from './PettyCash';
import BankStatement from './BankStatement';
import Contact from './Contact';
import CalendarView from './Calendar';
import DocStorage from './DocStorage';
import PortalAccounts from './PortalAccounts';
import Services from './Services';
import TakenReturn from './TakenReturn';
import Peak from './Peak';
import Tax from './Tax';
import Bookkeeping from './Bookkeeping';
import AccountingBooks from './AccountingBooks';
import Compliance from './Compliance';
import AIWReports from './AIWReports';
import ExternalPortal from './ExternalPortal';
import MarketingPortal from './MarketingPortal';
import ClientPortal from './ClientPortal';
import ContractSign from './ContractSign';
import Leads from './Leads';
import AiMarketing from './AiMarketing';
import DueDiligence, { DDPublicPage } from './DueDiligence';
import Workflows from './Workflows';
import { GlobalTasksAlert } from './MyTasks';
import WfShare from './WfShare';
import PandaMarketing from './PandaMarketing';
import SlfProperty from './SlfProperty';
import ServiceDesk from './ServiceDesk';
import AiCounsel from './AiCounsel';
import StaffDashboard from './StaffDashboard';
import FinanceReport from './FinanceReport';
import ClientDashboard from './ClientDashboard';
import { JobsPage } from './Jobs';
import Toast from './Toast';
import { DocNoHost } from './DocNoLink';
import VersionPage from './VersionPage';
import Bugify from './Bugify';
import JobStatusConfirm from './JobStatusConfirm';
import JobCommentPing from './JobCommentPing';
import JoyWidget, { JoyKiosk } from './Joy/JoyWidget';
import UploadIndicator from './UploadIndicator';
import DropProposals from './DropProposals';
import NewMailToast from './NewMailToast';

function CenteredSpinner() {
  return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)' }}>
      <i className="ti ti-loader" style={{ fontSize: 28, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
      <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </div>
  );
}

function NoAccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, background: 'var(--bg-app)' }}>
      <i className="ti ti-lock" style={{ fontSize: 34, color: 'var(--text-tertiary)' }} aria-hidden />
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{t('no_access')}</div>
      <button onClick={() => navigate('/')} style={{ padding: '8px 16px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
        {t('back_to_portal')}
      </button>
    </div>
  );
}

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuthenticated, loadMe } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Already signed in → go to portal.
  useEffect(() => { if (localStorage.getItem('mf_token')) navigate('/', { replace: true }); }, []); // eslint-disable-line

  const handle = async () => {
    if (!email || !password) return setError(t('login_fill_fields'));
    setLoading(true); setError('');
    try {
      const res = await api.login(email, password);
      setAuthenticated(res.token);
      await loadMe();
      navigate('/', { replace: true });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-app)' }}>
      <div style={{ width: 380, background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border)', padding: '32px 28px', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src="/logo.png" alt="SLF" style={{ width: 40, height: 40, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{t('portal_title')}</div>
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('portal_subtitle')}</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('emailAddress')}</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@company.com" onKeyDown={e => e.key === 'Enter' && handle()} style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{t('field_password')}</label>
            <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && handle()} style={{ width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none' }} />
          </div>
          {error && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{error}</div>}
          <button onClick={handle} disabled={loading} style={{ padding: '10px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? t('login_wait') : t('login_signin')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Gate: require a token, then load the current user's profile before rendering.
function AuthGate() {
  const { me, loadMe } = useStore();
  const token = localStorage.getItem('mf_token');
  const [checking, setChecking] = useState(!!token && !me);

  useEffect(() => {
    if (token && !me) loadMe().finally(() => setChecking(false));
    else setChecking(false);
  }, []); // eslint-disable-line

  if (!token) return <Navigate to="/login" replace />;
  if (checking || !me) return <CenteredSpinner />;
  // External accountants/auditors only ever see their own portal — never the internal app.
  if (me.role === 'external') return <ExternalPortal />;
  // Marketing role → a mobile-first, fully read-only portal (own clients' basic info + own finance share).
  if (me.role === 'marketing') return <MarketingPortal />;
  // The auditor login (Bugify #3898b242) → SLF Tax only, and none of the office overlays below (Joy,
  // the tasks alert) come along.
  if (me.role === 'auditor') return <AuditorGate />;
  // The joy@ login is a kiosk — ONLY Joy, no portal — so she can live on any spare desktop.
  if ((me.email || '').toLowerCase() === 'joy@slf-accounting.co') return <JoyKiosk />;
  // Joy floats over every authenticated internal page; the widget self-gates (office users + on).
  // GlobalTasksAlert rides along so an overdue job / long-unmatched receipt reaches whoever is working,
  // whatever module page they are on — it only overlays, never navigates or reloads.
  // DropProposals: the AI's name-and-folder proposal for each document dropped into Drop File, whoever
  // dropped it (report df26fdac) — shown to Calem alone; it self-gates to him.
  // NewMailToast: the new-mail pop-up at the bottom right of every page (Calem, report 2e5c8ac7) — new
  // Inbox mail of the user's starred account; it self-gates to the email module.
  return <><Outlet /><JoyWidget /><GlobalTasksAlert who={me.email} /><DropProposals /><NewMailToast /></>;
}

// The auditor's whole app is SLF Tax: '/', every other /m/ page and /admin land on it.
function AuditorGate() {
  const loc = useLocation();
  // Its menu has no Sandbox tile, so a Sandbox switched on before the role existed would have no way off.
  useEffect(() => { if (isSandbox()) setSandbox(false); }, []);
  return loc.pathname === '/m/tax' ? <Outlet /> : <Navigate to="/m/tax" replace />;
}

// The job popups are mounted outside AuthGate (see App), so they are kept off the auditor login —
// SLF Tax only (Bugify #3898b242) — here instead.
function NotForAuditor({ children }: { children: React.ReactElement }) {
  const auditor = useStore(s => s.me?.role === 'auditor');
  return auditor ? null : children;
}

function RequireModule({ k, children }: { k: string; children: React.ReactElement }) {
  const { me } = useStore();
  if (!me) return null;
  // Marketing owners (no finance_report module) may still open the Finance Report — they see only their
  // own marketing tabs; the firm overview stays grant-gated server-side.
  const marketingPass = k === 'finance_report' && (me as any).hasMarketing;
  // Straight off the server's list (see the menu in Portal) — an admin already has every module in
  // it, so no role shortcut is needed, and a module reserved to one person stays shut by URL too.
  if (!me.modules.includes(k) && !marketingPass) return <NoAccess />;
  return children;
}

// Guard for the container pages (Panda Marketing / SLF Property): they aren't real permissions, so grant
// access if the user holds ANY of the folded-in sub-modules. Each tab is gated again inside the page.
function RequireAny({ ks, children }: { ks: string[]; children: React.ReactElement }) {
  const { me } = useStore();
  if (!me) return null;
  if (me.role !== 'admin' && !ks.some(k => me.modules.includes(k))) return <NoAccess />;
  return children;
}

function RequireAdmin({ children }: { children: React.ReactElement }) {
  const { me } = useStore();
  if (!me) return null;
  if (me.role !== 'admin') return <NoAccess />;
  return children;
}

// /m/:key — stub modules (email has its own route above this one).
function ModuleStubRoute() {
  const { key = '' } = useParams();
  const { me } = useStore();
  if (!isValidModule(key)) return <Navigate to="/" replace />;
  if (!me) return null;
  if (me.role !== 'admin' && !me.modules.includes(key)) return <NoAccess />;
  return <ModuleStub moduleKey={key} />;
}

// Keep the browser tab title in sync with the current page so each open tab is distinguishable
// (instead of every tab showing just "SLF System").
function DocumentTitle() {
  const loc = useLocation();
  const { t } = useTranslation();
  useEffect(() => {
    const base = 'SLF System';
    const p = loc.pathname;
    const sp = new URLSearchParams(loc.search);
    let label = '';
    if (p === '/' || p === '') {
      // Same label keys as the Portal's tab strip (DASH_TABS / DOC_TABS / CALENDAR_TAB / EMAIL_TAB) —
      // a tab missing here falls back to "Legal & Accounting", so every new Portal tab needs an entry.
      const dash: Record<string, string> = { quotation: 'mod_quotation', invoice: 'mod_invoice', receipt: 'mod_receipt', outbox: 'mod_outbox', bank_acc: 'mod_bank_acc', bank_legal: 'mod_bank_legal', bank_client: 'mod_bank_client', active_jobs: 'mod_active_jobs', closed_jobs: 'mod_closed_jobs', pettycash: 'mod_pettycash', taken_return: 'mod_taken_return', doc_storage: 'mod_upload_doc', calendar: 'mod_calendar', email: 'tab_email', starred: 'mod_starred' };
      const tab = sp.get('tab') || '';
      label = dash[tab] ? t(dash[tab]) : t('portal_subtitle', { defaultValue: 'Dashboard' });
    } else if (p.startsWith('/m/')) {
      const key = p.split('/')[2] || '';
      // Email reads "Email" like its Portal tab, not the "PandaAI™ Email" module name (Bugify #a2db2014).
      label = key === 'email' ? t('tab_email') : t(`mod_${key}`, { defaultValue: key });
      if (key === 'tax') {
        const secMap: Record<string, string> = { monthly: 'tx_tab_monthly', halfyear: 'tx_tab_halfyear', eoy: 'tx_tab_eoy', property: 'tx_property_tax' };
        const sec = sp.get('section') || ''; if (secMap[sec]) label = `${label} · ${t(secMap[sec])}`;
      }
    } else if (p.startsWith('/admin')) label = t('admin_panel', { defaultValue: 'Admin' });
    document.title = label ? `${label} · ${base}` : base;
  }, [loc.pathname, loc.search, t]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <DocumentTitle />
      {/* Folder uploads are driven from a module-level manager, so they keep running while the
          operator moves between modules; this card is their only UI and lives in the shell. */}
      <UploadIndicator />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/dd/view/:token" element={<DDPublicPage />} />{/* public client preview — no auth */}
        <Route path="/w/:token" element={<WfShare />} />{/* public workflow share page — no auth */}
        <Route path="/contract/:token" element={<ContractSign />} />
        <Route path="/portal/*" element={<ClientPortal />} />{/* client invoicing portal — self-managed auth */}{/* public contract e-signing — no auth */}
        <Route element={<AuthGate />}>
          <Route path="/" element={<Portal />} />
          <Route path="/m/email" element={<RequireModule k="email"><MailApp /></RequireModule>} />
          <Route path="/m/pettycash" element={<RequireModule k="pettycash"><PettyCash /></RequireModule>} />
          <Route path="/m/bank_acc" element={<RequireModule k="bank_acc"><BankStatement scope="accounting" moduleKey="bank_acc" /></RequireModule>} />
          <Route path="/m/bank_legal" element={<RequireModule k="bank_legal"><BankStatement scope="legal" moduleKey="bank_legal" /></RequireModule>} />
          <Route path="/m/contact" element={<RequireModule k="contact"><Contact /></RequireModule>} />
          <Route path="/m/calendar" element={<RequireModule k="calendar"><div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}><CalendarView /></div></RequireModule>} />
          <Route path="/m/doc_storage" element={<RequireModule k="doc_storage"><DocStorage /></RequireModule>} />
          <Route path="/m/portal_accounts" element={<RequireModule k="portal_accounts"><PortalAccounts /></RequireModule>} />
          <Route path="/m/services" element={<RequireModule k="services"><Services /></RequireModule>} />
          <Route path="/m/taken_return" element={<RequireModule k="taken_return"><TakenReturn /></RequireModule>} />
          <Route path="/m/fee_board" element={<RequireModule k="fee_board"><FeeBoardView /></RequireModule>} />
          <Route path="/m/peak" element={<RequireModule k="peak"><Peak /></RequireModule>} />
          <Route path="/m/tax" element={<RequireModule k="tax"><Tax /></RequireModule>} />
          <Route path="/m/version" element={<VersionPage />} />
          <Route path="/m/bugify" element={<Bugify />} />{/* shared module — everyone on staff */}
          <Route path="/m/bookkeeping" element={<RequireModule k="bookkeeping"><Bookkeeping /></RequireModule>} />
          <Route path="/m/accounting_books" element={<RequireModule k="accounting_books"><AccountingBooks /></RequireModule>} />
          <Route path="/m/compliance" element={<RequireModule k="compliance"><AIWReports /></RequireModule>} />
          <Route path="/m/compliance_legacy" element={<RequireModule k="compliance"><Compliance /></RequireModule>} />
          <Route path="/m/leads" element={<RequireModule k="leads"><Leads /></RequireModule>} />
          <Route path="/m/ai_marketing" element={<RequireModule k="ai_marketing"><AiMarketing /></RequireModule>} />
          <Route path="/m/due_diligence" element={<RequireModule k="due_diligence"><DueDiligence /></RequireModule>} />
          <Route path="/m/ai_counsel" element={<RequireModule k="ai_counsel"><AiCounsel /></RequireModule>} />
          <Route path="/m/panda_marketing" element={<RequireAny ks={['leads', 'ai_marketing', 'social_poster', 'compliance']}><PandaMarketing /></RequireAny>} />
          <Route path="/m/slf_property" element={<RequireAny ks={['due_diligence', 'property_transfer', 'poa', 'lease_reg', 'workflows']}><SlfProperty /></RequireAny>} />
          <Route path="/m/workflows" element={<RequireModule k="workflows"><Workflows /></RequireModule>} />
          {/* The three desk consoles (owner 2026-09-23) — Legal Matters' twins on the same engine. */}
          <Route path="/m/slf_visa" element={<RequireModule k="slf_visa"><ServiceDesk desk="visa" /></RequireModule>} />
          <Route path="/m/slf_company" element={<RequireModule k="slf_company"><ServiceDesk desk="company" /></RequireModule>} />
          <Route path="/m/slf_license" element={<RequireModule k="slf_license"><ServiceDesk desk="license" /></RequireModule>} />
          <Route path="/m/active_jobs" element={<RequireModule k="active_jobs"><JobsPage view="active" /></RequireModule>} />
          <Route path="/m/closed_jobs" element={<RequireModule k="closed_jobs"><JobsPage view="closed" /></RequireModule>} />
          <Route path="/m/staff_dashboard" element={<RequireModule k="staff_dashboard"><StaffDashboard /></RequireModule>} />
          <Route path="/m/finance_report" element={<RequireModule k="finance_report"><FinanceReport /></RequireModule>} />
          <Route path="/m/client_dashboard" element={<ClientDashboard />} />
          <Route path="/m/:key" element={<ModuleStubRoute />} />
          <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <Toast />
      {/* The quotation / invoice / receipt a clicked QO- / IV- / RE- number opens (DocNoLink). */}
      <DocNoHost />
      {/* Before JobStatusConfirm on purpose: same layer, so a status email waiting on its confirmation
          stays on top of a colleague's question. */}
      <NotForAuditor><JobCommentPing /></NotForAuditor>
      <NotForAuditor><JobStatusConfirm /></NotForAuditor>
    </BrowserRouter>
  );
}
